import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { highlightIndexAfterRemoval } from "./dispatchModel";
import type { DispatchTableRow } from "./dispatchTableRow";

/**
 * Index of the row a move by `delta` rows lands on: the first/last row when nothing is highlighted, clamped otherwise.
 *
 * @param currentIndex - The highlighted row's index, or `-1` for none.
 * @param delta - Whole rows to move, down when positive: one for the row keys, a screenful for the page keys.
 * @param rowCount - How many rows the list holds.
 */
export function nextHighlightIndex(currentIndex: number, delta: number, rowCount: number): number | null {
  if (rowCount <= 0) return null;
  if (currentIndex < 0) return delta > 0 ? 0 : rowCount - 1;
  return Math.min(rowCount - 1, Math.max(0, currentIndex + delta));
}

/**
 * The rows a Shift-arrow run may anchor on when nothing is highlighted, in the order they are tried.
 */
export type HighlightAnchorCandidates = {
  /** The keyboard's row; always wins while there is one. */
  highlightedId: string | null;
  /** The row whose History or Context panel is open. */
  openRowId: string | null;
  /** The row under the pointer. */
  hoveredRowId: string | null;
};

/**
 * Index of the row a Shift-arrow run starts from: the highlighted row, else the open panel's row, else the row under
 * the pointer, else `-1` so the run comes in at the end of the list the arrow points away from. A candidate no longer
 * in the list is skipped rather than stranding the run.
 */
export function highlightAnchorIndex(rows: readonly DispatchTableRow[], candidates: HighlightAnchorCandidates): number {
  for (const id of [candidates.highlightedId, candidates.openRowId, candidates.hoveredRowId]) {
    if (!id) continue;
    const index = rows.findIndex((row) => row.id === id);
    if (index !== -1) return index;
  }
  return -1;
}

/**
 * The row that becomes highlighted once the row at `currentIndex` is handled: the next one, else the previous, else
 * nothing. Computed before the handled row leaves the list, so the highlight never lands on a row that is about to
 * disappear.
 */
export function advancedHighlightIndex(currentIndex: number, rowCount: number): number | null {
  if (currentIndex < 0 || rowCount <= 1) return null;
  return currentIndex < rowCount - 1 ? currentIndex + 1 : currentIndex - 1;
}

/**
 * The roving highlight over the visible Dispatch rows, by row id: which row the keyboard acts on, moved by row and
 * advanced past a handled row. When its row leaves the list (read from History, cleared by a teammate, filtered out)
 * the highlight lands on the row now in that position, so the dispatcher's hands never leave the keys; it clears only
 * when the list is empty. Group headers are not rows, so the keyboard steps over them. `move` goes any whole number
 * of rows, clamped to the list, and `advance` steps past the handled row; both hand back the row they landed on, so
 * the caller can act on the selection it just made (an open popover follows it).
 */
export function useDispatchHighlight(rows: readonly DispatchTableRow[]): {
  highlightedId: string | null;
  highlighted: DispatchTableRow | null;
  highlight: (id: string | null) => void;
  move: (delta: number) => DispatchTableRow | null;
  advance: () => DispatchTableRow | null;
} {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const highlightedIndex = useMemo(
    () => (highlightedId ? rows.findIndex((row) => row.id === highlightedId) : -1),
    [rows, highlightedId],
  );

  // The position the highlight last held, so a row that vanishes hands the highlight to its successor.
  const lastIndexRef = useRef(-1);
  if (highlightedIndex !== -1) lastIndexRef.current = highlightedIndex;

  // An empty list is not a verdict on the highlight: rows are still loading, or a filter hid everything for a moment,
  // and a deep link that highlighted its driver before bootstrap finished must survive until the rows arrive.
  useEffect(() => {
    if (!highlightedId || highlightedIndex !== -1 || rows.length === 0) return;
    const landing = highlightIndexAfterRemoval(lastIndexRef.current, rows.length);
    setHighlightedId(landing == null ? null : (rows[landing]?.id ?? null));
  }, [highlightedId, highlightedIndex, rows]);

  const move = useCallback(
    (delta: number) => {
      const next = nextHighlightIndex(highlightedIndex, delta, rows.length);
      if (next == null) return null;
      setHighlightedId(rows[next].id);
      return rows[next];
    },
    [rows, highlightedIndex],
  );

  const advance = useCallback(() => {
    const next = advancedHighlightIndex(highlightedIndex, rows.length);
    const landed = next == null ? null : rows[next];
    setHighlightedId(landed?.id ?? null);
    return landed;
  }, [rows, highlightedIndex]);

  return {
    highlightedId,
    highlighted: highlightedIndex === -1 ? null : rows[highlightedIndex],
    highlight: setHighlightedId,
    move,
    advance,
  };
}
